import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Chip,
  Grid,
  MenuItem,
  IconButton,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { API } from '../services/api';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import ConfirmDialog from '../components/ConfirmDialog';
import ActionButton from '../components/ActionButton';
import { showSuccess, showError } from '../utils/notifications';

const MotionBox = motion(Box);

function IoTDevices() {
  const [devices, setDevices] = useState([]);
  const [models, setModels] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deviceTypeFilter, setDeviceTypeFilter] = useState('all');
  const [connectionStatusFilter, setConnectionStatusFilter] = useState('all');
  const [operationalStatusFilter, setOperationalStatusFilter] = useState('all');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [formData, setFormData] = useState({
    device_name: '',
    device_code: '',
    model_id: '',
    equipment_id: '',
    device_type: '',
    manufacturer: '',
    device_model: '',
    measurement_unit_id: '',
    protocol: '',
    connection_string: '',
    api_endpoint: '',
    calibration_date: '',
    next_calibration: '',
    connection_status: 1,
    operational_status: 1,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [devicesRes, modelsRes, equipmentRes, unitsRes] = await Promise.all([
        API.iotDevices.list(),
        API.models.list(),
        API.equipment.list(),
        API.units.list(),
      ]);
      setDevices(devicesRes?.data || []);
      setModels(modelsRes?.data || []);
      setEquipment(equipmentRes?.data || []);
      setUnits(unitsRes?.data || []);
    } catch (err) {
      showError('Failed to load IoT devices');
      console.error('IoT devices fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (device = null) => {
    if (device) {
      setEditingDevice(device);
      setFormData({
        device_name: device.device_name || '',
        device_code: device.device_code || '',
        model_id: device.model_id || '',
        equipment_id: device.equipment_id || '',
        device_type: device.device_type || '',
        manufacturer: device.manufacturer || '',
        device_model: device.device_model || '',
        measurement_unit_id: device.measurement_unit_id || '',
        protocol: device.protocol || '',
        connection_string: device.connection_string || '',
        api_endpoint: device.api_endpoint || '',
        calibration_date: device.calibration_date ? device.calibration_date.split('T')[0] : '',
        next_calibration: device.next_calibration ? device.next_calibration.split('T')[0] : '',
        connection_status: device.connection_status !== undefined ? device.connection_status : 1,
        operational_status: device.operational_status !== undefined ? device.operational_status : 1,
      });
    } else {
      setEditingDevice(null);
      setFormData({
        device_name: '',
        device_code: '',
        model_id: '',
        equipment_id: '',
        device_type: '',
        manufacturer: '',
        device_model: '',
        measurement_unit_id: '',
        protocol: '',
        connection_string: '',
        api_endpoint: '',
        calibration_date: '',
        next_calibration: '',
        connection_status: 1,
        operational_status: 1,
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingDevice(null);
    setFormData({
      device_name: '',
      device_code: '',
      model_id: '',
      equipment_id: '',
      device_type: '',
      manufacturer: '',
      device_model: '',
      measurement_unit_id: '',
      protocol: '',
      connection_string: '',
      api_endpoint: '',
      calibration_date: '',
      next_calibration: '',
      connection_status: 1,
      operational_status: 1,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingDevice) {
        await API.iotDevices.update(editingDevice.device_id, formData);
        showSuccess('Device updated successfully!');
      } else {
        await API.iotDevices.create(formData);
        showSuccess('Device created successfully!');
      }
      handleCloseDialog();
      fetchData();
    } catch (err) {
      const errorMessage = editingDevice 
        ? 'Failed to update device' 
        : 'Failed to create device';
      showError(errorMessage);
      console.error('Device submit error:', err);
    }
  };

  const handleDelete = async (deviceId) => {
    setConfirmDialog({ open: true, id: deviceId });
  };

  const handleConfirmDelete = async () => {
    try {
      await API.iotDevices.delete(confirmDialog.id);
      showSuccess('Device deleted successfully!');
      fetchData();
    } catch (err) {
      showError('Failed to delete device');
      console.error('Device delete error:', err);
    } finally {
      setConfirmDialog({ open: false, id: null });
    }
  };

  const getModelName = (modelId) => {
    const model = models.find(m => m.model_id === modelId);
    return model ? model.model_name : 'Unknown';
  };

  const getEquipmentName = (equipmentId) => {
    if (!equipmentId) return 'N/A';
    const eq = equipment.find(e => e.equipment_id === equipmentId);
    return eq ? eq.equipment_name : 'Unknown';
  };

  const getConnectionStatusLabel = (status) => {
    switch (status) {
      case 1: return 'Connected';
      case 0: return 'Disconnected';
      default: return 'Unknown';
    }
  };

  const getConnectionStatusColor = (status) => {
    switch (status) {
      case 1: return { bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7' }; // Connected - Green
      case 0: return { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' }; // Disconnected - Red
      default: return { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' };
    }
  };

  const getOperationalStatusLabel = (status) => {
    switch (status) {
      case 1: return 'Active';
      case 0: return 'Inactive';
      default: return 'Unknown';
    }
  };

  const getOperationalStatusColor = (status) => {
    switch (status) {
      case 1: return { bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7' }; // Active - Green
      case 0: return { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' }; // Inactive - Gray
      default: return { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' };
    }
  };

  const filteredDevices = devices.filter(device => {
    const matchesSearch = device.device_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.device_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.device_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDeviceType = deviceTypeFilter === 'all' || device.device_type === deviceTypeFilter;
    const matchesConnectionStatus = connectionStatusFilter === 'all' || device.connection_status === parseInt(connectionStatusFilter);
    const matchesOperationalStatus = operationalStatusFilter === 'all' || device.operational_status === parseInt(operationalStatusFilter);
    
    return matchesSearch && matchesDeviceType && matchesConnectionStatus && matchesOperationalStatus;
  });

  const uniqueDeviceTypes = [...new Set(devices.map(d => d.device_type).filter(Boolean))];

  const columns = [
    { 
      field: 'device_name', 
      headerName: 'Device Name', 
      width: '18%',
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.device_name}
        </Typography>
      )
    },
    { 
      field: 'device_code', 
      headerName: 'Device Code', 
      width: '12%',
      render: (row) => (
        <Chip 
          label={row.device_code} 
          size="small" 
          sx={{ 
            fontWeight: 600,
            fontSize: '0.7rem',
            backgroundColor: '#EEF2FF',
            color: '#4F46E5',
            border: '1px solid #C7D2FE',
          }}
        />
      )
    },
    { 
      field: 'device_type', 
      headerName: 'Type', 
      width: '12%',
      render: (row) => row.device_type 
    },
    { 
      field: 'manufacturer', 
      headerName: 'Manufacturer', 
      width: '12%',
      render: (row) => row.manufacturer || '-'
    },
    { 
      field: 'device_model', 
      headerName: 'Model', 
      width: '10%',
      render: (row) => row.device_model || '-'
    },
    { 
      field: 'connection_status', 
      headerName: 'Connection', 
      width: '12%',
      render: (row) => {
        const colors = getConnectionStatusColor(row.connection_status);
        return (
          <Chip 
            label={getConnectionStatusLabel(row.connection_status)} 
            size="small"
            sx={{ 
              fontWeight: 600,
              fontSize: '0.7rem',
              backgroundColor: colors.bg,
              color: colors.color,
              border: `1px solid ${colors.border}`,
            }}
          />
        );
      }
    },
    { 
      field: 'operational_status', 
      headerName: 'Operational', 
      width: '12%',
      render: (row) => {
        const colors = getOperationalStatusColor(row.operational_status);
        return (
          <Chip 
            label={getOperationalStatusLabel(row.operational_status)} 
            size="small"
            sx={{ 
              fontWeight: 600,
              fontSize: '0.7rem',
              backgroundColor: colors.bg,
              color: colors.color,
              border: `1px solid ${colors.border}`,
            }}
          />
        );
      }
    },
    { 
      field: 'last_communication', 
      headerName: 'Last Comm', 
      width: '15%',
      render: (row) => row.last_communication ? new Date(row.last_communication).toLocaleString() : '-'
    },
    { 
      field: 'created_at', 
      headerName: 'Created', 
      width: '12%',
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'
    },
  ];

  const filterFields = [
    {
      name: 'deviceType',
      label: 'Device Type',
      value: deviceTypeFilter,
      onChange: setDeviceTypeFilter,
      options: [
        { value: 'all', label: 'All Types' },
        ...uniqueDeviceTypes.map(type => ({
          value: type,
          label: type,
        })),
      ],
    },
    {
      name: 'connectionStatus',
      label: 'Connection Status',
      value: connectionStatusFilter,
      onChange: setConnectionStatusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: '1', label: 'Connected' },
        { value: '0', label: 'Disconnected' },
      ],
    },
    {
      name: 'operationalStatus',
      label: 'Operational Status',
      value: operationalStatusFilter,
      onChange: setOperationalStatusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: '1', label: 'Active' },
        { value: '0', label: 'Inactive' },
      ],
    },
  ];

  const TableSkeleton = () => (
    <Box sx={{ width: '100%' }}>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} height={60} sx={{ my: 1 }} />
      ))}
    </Box>
  );

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            IoT Devices
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} disabled>
            Add Device
          </Button>
        </Box>
        <TableSkeleton />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          IoT Devices
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Device
        </Button>
      </Box>

      <FilterBar 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterFields={filterFields}
      />

      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <DataTable
          columns={columns}
          data={filteredDevices}
          onEdit={handleOpenDialog}
          onDelete={handleDelete}
          getRowId={(row) => row.device_id}
          emptyMessage="No devices found"
        />
      </MotionBox>

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {editingDevice ? 'Edit IoT Device' : 'Add New IoT Device'}
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Device Name"
                value={formData.device_name}
                onChange={(e) => setFormData({ ...formData, device_name: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Device Code"
                value={formData.device_code}
                onChange={(e) => setFormData({ ...formData, device_code: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Model"
                value={formData.model_id}
                onChange={(e) => setFormData({ ...formData, model_id: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                {models.map((model) => (
                  <MenuItem key={model.model_id} value={model.model_id}>
                    {model.model_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Equipment (Optional)"
                value={formData.equipment_id}
                onChange={(e) => setFormData({ ...formData, equipment_id: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value="">None</MenuItem>
                {equipment.map((eq) => (
                  <MenuItem key={eq.equipment_id} value={eq.equipment_id}>
                    {eq.equipment_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Device Type"
                value={formData.device_type}
                onChange={(e) => setFormData({ ...formData, device_type: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manufacturer"
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Device Model"
                value={formData.device_model}
                onChange={(e) => setFormData({ ...formData, device_model: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Measurement Unit"
                value={formData.measurement_unit_id}
                onChange={(e) => setFormData({ ...formData, measurement_unit_id: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value="">None</MenuItem>
                {units.map((unit) => (
                  <MenuItem key={unit.unit_id} value={unit.unit_id}>
                    {unit.unit_name} ({unit.unit_symbol})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Protocol"
                value={formData.protocol}
                onChange={(e) => setFormData({ ...formData, protocol: e.target.value })}
                placeholder="e.g., MQTT, HTTP, ModBus"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Connection String"
                value={formData.connection_string}
                onChange={(e) => setFormData({ ...formData, connection_string: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="API Endpoint"
                value={formData.api_endpoint}
                onChange={(e) => setFormData({ ...formData, api_endpoint: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Calibration Date"
                type="date"
                value={formData.calibration_date}
                onChange={(e) => setFormData({ ...formData, calibration_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Next Calibration"
                type="date"
                value={formData.next_calibration}
                onChange={(e) => setFormData({ ...formData, next_calibration: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Connection Status"
                value={formData.connection_status}
                onChange={(e) => setFormData({ ...formData, connection_status: parseInt(e.target.value) })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value={1}>Connected</MenuItem>
                <MenuItem value={0}>Disconnected</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Operational Status"
                value={formData.operational_status}
                onChange={(e) => setFormData({ ...formData, operational_status: parseInt(e.target.value) })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <ActionButton variant="outlined" onClick={handleCloseDialog}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" onClick={handleSubmit}>
            {editingDevice ? 'Update' : 'Create'}
          </ActionButton>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete Device"
        message="Are you sure you want to delete this device? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDialog({ open: false, id: null })}
      />
    </Box>
  );
}

export default IoTDevices;